import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Faltan las variables de entorno de Supabase');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface UserUsage {
  id: string;
  user_id: string;
  student_id: string | null;
  sessions_used: number; // For Live (Voice) Sessions
  chat_sessions_used: number; // For Text Chat Sessions
  last_reset_date: string;
  period_type: 'weekly' | 'monthly';
}

export const getUserUsage = async (userId: string, studentId?: string): Promise<UserUsage | null> => {
  let query = supabase
    .from('user_usage')
    .select('*')
    .eq('user_id', userId);

  if (studentId) {
    query = query.eq('student_id', studentId);
  } else {
    query = query.is('student_id', null);
  }

  const { data, error } = await query.single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching usage:', error);
  }
  return data;
};

export const initializeUserUsage = async (userId: string, plan: string, studentId?: string): Promise<UserUsage | null> => {
  // Family/Standard resets weekly by design (see useLiveSessionUsage.ts); Premium's cap is a
  // soft monthly safety ceiling, not a weekly-recurring one, so it belongs in the monthly group.
  const periodType = (plan === 'basic' || plan === 'trial' || plan === 'premium') ? 'monthly' : 'weekly';

  const { data, error } = await supabase
    .from('user_usage')
    .insert({
      user_id: userId,
      student_id: studentId || null,
      period_type: periodType,
      sessions_used: 0,
      chat_sessions_used: 0
    })
    .select()
    .single();

  if (error) console.error('Error init usage:', error);
  return data;
};

export const incrementUserUsageValue = async (userId: string, studentId: string | undefined, type: 'live' | 'chat', value: number): Promise<UserUsage | null> => {
  const current = await getUserUsage(userId, studentId);
  if (!current) return null;

  const updateField = type === 'live' ? 'sessions_used' : 'chat_sessions_used';
  const newValue = (current[updateField] || 0) + value;

  const { data, error } = await supabase
    .from('user_usage')
    .update({ [updateField]: newValue })
    .eq('id', current.id)
    .select()
    .single();

  if (error) console.error(`Error incrementing ${type} usage by ${value}:`, error);
  return data;
};

export const incrementUserUsage = async (userId: string, studentId?: string, type: 'live' | 'chat' = 'live'): Promise<UserUsage | null> => {
  return incrementUserUsageValue(userId, studentId, type, 1);
};

export const checkResetNeeded = async (usage: UserUsage): Promise<UserUsage> => {
  const now = new Date();
  const lastReset = new Date(usage.last_reset_date);
  let shouldReset = false;

  if (usage.period_type === 'weekly') {
    // Reset if it's been more than 7 days
    const diffDays = (now.getTime() - lastReset.getTime()) / (1000 * 3600 * 24);
    if (diffDays >= 7) shouldReset = true;
  } else {
    // Monthly
    if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) {
      shouldReset = true;
    }
  }

  if (shouldReset) {
    const { data } = await supabase
      .from('user_usage')
      .update({
        sessions_used: 0,
        chat_sessions_used: 0,
        last_reset_date: now.toISOString()
      })
      .eq('id', usage.id)
      .select()
      .single();
    return data || usage;
  }

  return usage;
};

// Tipos de base de datos
export interface ParentProfile {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  subscription_plan: string;
  // Idioma preferido para los correos automaticos generados del servidor (reportes semanales,
  // confirmacion de cuenta, etc.) -- persistido en DB, no solo en localStorage, para que un
  // proceso de fondo sin sesion de navegador (ej. el cron de reportes semanales) sepa en que
  // idioma escribirle a cada padre. Ver supabase/migrations/20260901_add_preferred_language.sql.
  preferred_language: 'es' | 'en';
  created_at: string;
  updated_at: string;
}

export interface Student {
  id: string;
  parent_id: string;
  name: string;
  age: number;
  grade: string;
  avatar_url?: string;
  total_sessions: number;
  total_achievements: number;
  completed_tasks?: number;
  study_hours?: number;
  average_grade?: number;
  created_at: string;
}

export interface HomeworkUpload {
  id: string;
  student_id: string;
  parent_id: string;
  title: string;
  description: string;
  subject: string;
  file_url: string | null;
  file_type: string | null;
  ai_analysis: string | null;
  status: 'pending' | 'completed';
  created_at: string;
}

export interface TutoringSession {
  id: string;
  student_id: string;
  homework_id?: string;
  subject: string;
  duration_minutes: number;
  questions_asked: number;
  hints_given: number;
  completion_percentage: number;
  ai_feedback?: string;
  session_summary?: string;
  started_at: string;
  ended_at?: string;
}

export interface Achievement {
  id: string;
  student_id: string;
  title: string;
  description?: string;
  badge_icon?: string;
  earned_at: string;
}
